import React from 'react'
import { Box, Flex, Text, Tooltip } from '@workwhile/ui'
import { CopyableLink } from 'components/Shared/CopyableLink'
import {
  getHighnoteAvailableFundLabel,
  HighnoteUserPayment,
  isHighnoteFundAvailablePayment,
  isPayoutPayment,
  UserPaymentStatus,
} from 'api/payments/getUserPayments'
import { usePayoutSummary } from 'queries/payments/usePayoutSummary'
import { PayoutTooltipContent } from './PayoutTooltipContent'
import { ProviderDetailLine } from './ProviderDetailLine'
import { RelatedPaymentsTooltipContent } from './RelatedPaymentsTooltipContent'
import { getPayoutStatusLabel } from '../../utils/labels'

type HighnoteProviderCellProps = {
  payment: HighnoteUserPayment
}

export const HighnoteProviderCell = ({
  payment,
}: HighnoteProviderCellProps) => {
  const payoutSummaryPaymentId =
    payment.payoutId ?? (isPayoutPayment(payment.eventType) ? payment.id : null)
  const hasInlinePayoutData =
    payment.payout != null || (payment.relatedPayments?.length ?? 0) > 0
  const { data: fetchedPayoutSummary } = usePayoutSummary(
    payoutSummaryPaymentId,
    {
      enabled: payoutSummaryPaymentId != null && !hasInlinePayoutData,
    }
  )

  const linkedPayout = payment.payout ?? fetchedPayoutSummary ?? null
  const relatedPayments = payment.relatedPayments?.length
    ? payment.relatedPayments
    : fetchedPayoutSummary?.relatedPayments ??
      linkedPayout?.relatedPayments ??
      []

  return (
    <Flex flexDirection="column" gap={0.5}>
      <Text fontWeight="bold">Highnote</Text>

      {payment.payoutId ? (
        linkedPayout ? (
          <Tooltip
            variant="neutral"
            content={
              <PayoutTooltipContent
                payout={linkedPayout}
                sourcePaymentId={payment.id}
              />
            }
          >
            <Box as="span" style={{ display: 'inline-block' }}>
              <ProviderDetailLine
                label={`${getPayoutStatusLabel(linkedPayout.status)}${
                  linkedPayout.status === UserPaymentStatus.COMPLETED
                    ? ' payment ID'
                    : ''
                }`}
              >
                <CopyableLink
                  label={payment.payoutId}
                  link={`/user_payments?payment-id=${payment.payoutId}`}
                />
              </ProviderDetailLine>
            </Box>
          </Tooltip>
        ) : (
          <ProviderDetailLine label="Payout">
            <CopyableLink
              label={payment.payoutId}
              link={`/user_payments?payment-id=${payment.payoutId}`}
            />
          </ProviderDetailLine>
        )
      ) : null}

      {isPayoutPayment(payment.eventType) && relatedPayments.length > 0 ? (
        <Tooltip
          variant="neutral"
          content={
            <RelatedPaymentsTooltipContent
              relatedPayments={relatedPayments}
              payoutAmount={linkedPayout?.amount ?? payment.amount}
              payoutStatus={linkedPayout?.status ?? payment.status}
            />
          }
        >
          <Box as="span" style={{ display: 'inline-block' }}>
            <ProviderDetailLine label="Earning payment IDs">
              {relatedPayments.map((relatedPayment, index) => (
                <React.Fragment key={relatedPayment.id}>
                  {index > 0 ? ', ' : null}
                  <CopyableLink
                    label={relatedPayment.id}
                    link={`/user_payments?payment-id=${relatedPayment.id}`}
                  />
                </React.Fragment>
              ))}
            </ProviderDetailLine>
          </Box>
        </Tooltip>
      ) : null}

      {isHighnoteFundAvailablePayment(payment) ? (
        <Text fontSize="sm">
          {getHighnoteAvailableFundLabel(payment.eventType)}
        </Text>
      ) : null}
    </Flex>
  )
}
